import styled from '@emotion/styled'

import { Color, theme } from '../../../constants'
import { Badge, Div, Flex, Text } from '../../../elements'
import { BemloPlusIcon } from '../../../svgs/BemloPlusIcon'
import { useTranslations } from '../../../translate'
import { createStyles } from '../../../utils/create-styles'
import { CandidateViewMatchingVacancy, CandidateViewVacancy } from '../types'

const GREAT_MATCH_SCORE = 86

const BemloPlus = styled.div(
  createStyles({
    backgroundColor: Color.PURPLE,
    borderRadius: theme.border.radius.normal,
    flexShrink: 0,
    height: '32px',
    p: 2,
    width: '32px',
  }),
)

const VacancyElement = styled(Div)<{ compact?: boolean }>((props) => ({
  ...createStyles({
    alignItems: 'flex-start',
    cursor: 'pointer',
    flex: 'row',
    gap: 3,
    px: 3.5,
    py: 2.5,
    transition: theme.animation.transition,
  }),
  '&:hover': {
    backgroundColor: props.compact ? Color.LIGHTER_BLUE : Color.LIGHTEST_BLUE,
  },
}))

export type VacancyProps = {
  compact?: boolean
  matchingVacancy: CandidateViewMatchingVacancy
  onClick: (vacancy: CandidateViewVacancy) => void
}

export const Vacancy = ({
  compact,
  matchingVacancy,
  onClick,
}: VacancyProps) => {
  const { score, vacancy } = matchingVacancy

  const t = useTranslations()

  return (
    <VacancyElement
      compact={compact}
      onClick={() => onClick(matchingVacancy.vacancy)}
    >
      <BemloPlus>
        <BemloPlusIcon color={Color.WHITE} />
      </BemloPlus>
      <Flex col gap={1} grow={1}>
        <Flex justify="between">
          <Flex gap={1} items="center" wrap>
            <Text bold color={Color.BLUE} mr={2} size={compact ? 6.5 : 5.5}>
              {vacancy.title ?? `(${t.candidate_view.vacancies.no_title()})`}
            </Text>
            {score >= GREAT_MATCH_SCORE && (
              <Badge color="green" size="sm">
                {t.candidate_view.vacancies.n_percent_match(score)}
              </Badge>
            )}
          </Flex>
          {!compact && (
            <Text
              color={Color.DARK_GREY}
              size={6.5}
              styles={{ userSelect: 'none' }}
            >
              {t.$date.unix(vacancy.createdAt).fromNow()}
            </Text>
          )}
        </Flex>
        {vacancy.unit && (
          <Text bold size={6.5}>
            {vacancy.unit.name}
          </Text>
        )}
        <Text color={Color.DARK_GREY} size={6.5}>
          {vacancy.municipality && `${vacancy.municipality}, `}
          {t.$whichRegionAlternative(vacancy.region)}
        </Text>
        {vacancy.jobStartsAt && (
          <Text color={Color.DARK_GREY} size={6.5}>
            {t.$date.unix(vacancy.jobStartsAt).format('ll')} –
            {vacancy.jobEndsAt && (
              <> {t.$date.unix(vacancy.jobEndsAt).format('ll')}</>
            )}
          </Text>
        )}
      </Flex>
    </VacancyElement>
  )
}
